import * as React from 'react';

import { Box, Popover, TextField, Tooltip, Typography } from '@mui/material';

import {
  PERCENT_TOTAL,
  levelLabel,
  moveBoundary,
  setPercentage,
  type DimensionDraft,
} from './demographic';

/**
 * A distribution as one bar, rather than a column of numbers.
 *
 * What is being edited is a probability distribution, not a form: the segments
 * are the levels, their widths are their shares, and the bar is always full.
 * Dragging a boundary is a transfer between the two levels either side of it,
 * so the total cannot drift — the invariant lives in the operation rather than
 * in a check that runs afterwards and complains.
 *
 * Every drag has a keyboard equivalent. The boundaries are sliders, so arrows
 * move them a point at a time and Shift moves them ten; a segment can also be
 * clicked or entered to type a number outright, which is the faster path when
 * someone already knows the quota they want.
 */

type Props = {
  /** dimension label, for accessible names */
  label: string;
  /** levels in display order */
  levels: string[];
  weights: DimensionDraft;
  onChange: (next: DimensionDraft) => void;
  /**
   * Show the distribution without offering to change it.
   *
   * The frame of a saved translation is fixed, but it still has to be legible —
   * checking what a translation was rendered against is the main reason to open
   * one. So the bar is drawn the same way and only the handles go: no dividers,
   * no click-to-edit, and the segments stop being buttons rather than becoming
   * buttons that do nothing.
   */
  readOnly?: boolean;
};

/** Below this, a segment has no room for its own text. */
const LABEL_MIN_PERCENT = 9;

/**
 * One accent, stepped light to dark.
 *
 * The levels within a dimension are ordered — 18-20 comes before 21-24, and the
 * countries sit in a fixed vocabulary order — so a sequential ramp says
 * something true about them. Eight unrelated hues said only "these are
 * different", and spent the whole palette doing it.
 *
 * The range stops at 50% lightness so white text clears 4.5:1 on every step,
 * including the lightest.
 */
const segmentColour = (i: number, n: number): string =>
  n <= 1 ? 'hsl(212 72% 38%)' : `hsl(212 72% ${50 - (24 * i) / (n - 1)}%)`;

const DistributionBar: React.FC<Props> = ({
  label,
  levels,
  weights,
  onChange,
  readOnly = false,
}) => {
  const barRef = React.useRef<HTMLDivElement>(null);
  /** The listeners registered for the drag in progress, so they can be undone. */
  const dragRef = React.useRef<(() => void) | null>(null);
  const [editing, setEditing] = React.useState<string | null>(null);
  const [entry, setEntry] = React.useState('');

  const values = levels.map((l) => weights[l] ?? 0);

  /** Where the pointer sits along the bar, as a percentage of its width. */
  const percentAt = (clientX: number): number | null => {
    const rect = barRef.current?.getBoundingClientRect();
    if (!rect?.width) return null;
    const pct = ((clientX - rect.left) / rect.width) * PERCENT_TOTAL;
    return Number.isFinite(pct) ? pct : null;
  };

  /**
   * Each move is computed from where the drag started, not from the last frame.
   *
   * `moveBoundary` sets the boundary outright rather than nudging it, so the
   * closure holding the weights as they were at pointer-down is what makes the
   * drag land where the cursor is. Reading live weights instead would fold each
   * frame's rounding into the next one.
   */
  const startDrag = (index: number) => (e: React.PointerEvent) => {
    e.preventDefault();
    dragRef.current?.();

    const onMove = (ev: PointerEvent) => {
      const pct = percentAt(ev.clientX);
      if (pct !== null) onChange(moveBoundary(weights, levels, index, pct));
    };
    const stop = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', stop);
      // A touch or pen gesture the browser takes over for scrolling ends in
      // pointercancel, never pointerup. Without this the move listener stays
      // attached and the next unrelated movement keeps rewriting the weights.
      window.removeEventListener('pointercancel', stop);
      dragRef.current = null;
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
    dragRef.current = stop;

    const target = e.target as Element;
    if (target.setPointerCapture) target.setPointerCapture(e.pointerId);
  };

  // Unmounting mid-drag has to undo the listeners that were actually added,
  // which are not the ones this render would create.
  React.useEffect(() => () => dragRef.current?.(), []);

  const nudgeBoundary = (index: number, delta: number) => {
    const before = values.slice(0, index + 1).reduce((a, b) => a + b, 0);
    onChange(moveBoundary(weights, levels, index, before + delta));
  };

  const openEditor = (level: string) => {
    setEditing(level);
    setEntry(String(weights[level] ?? 0));
  };

  const commitEntry = () => {
    if (editing === null) return;
    const value = Number(entry);
    if (Number.isFinite(value)) {
      onChange(setPercentage(weights, levels, editing, value));
    }
    setEditing(null);
  };

  if (!levels.length) return null;

  // Cumulative offsets, so boundaries and segments agree on where they sit.
  const offsets = values.reduce<number[]>(
    (acc, v, i) => [...acc, acc[i] + v],
    [0],
  );

  return (
    <Box>
      <Box
        ref={barRef}
        role="group"
        aria-label={`${label} distribution`}
        sx={{
          position: 'relative',
          display: 'flex',
          height: 44,
          borderRadius: 1,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'grey.100',
          userSelect: 'none',
        }}
      >
        {levels.map((level, i) => {
          const value = values[i];
          const roomy = value >= LABEL_MIN_PERCENT;
          return (
            <Tooltip key={level} title={`${levelLabel(level)} — ${value}%`}>
              <Box
                component={readOnly ? 'div' : 'button'}
                type={readOnly ? undefined : 'button'}
                id={`seg-${label}-${level}`}
                aria-label={
                  readOnly
                    ? `${label} ${levelLabel(level)}, ${value} percent`
                    : `${label} ${levelLabel(level)}, ${value} percent. Enter to type a percentage.`
                }
                onClick={readOnly ? undefined : () => openEditor(level)}
                sx={{
                  flexGrow: 0,
                  // Exactly its share: the dividers are positioned from the
                  // same cumulative offsets, so a minimum width here would
                  // slide the segments out from under them. Thin segments stay
                  // grabbable because the divider hit area is 11px regardless,
                  // and readable because the legend below picks them up.
                  flexBasis: `${value}%`,
                  minWidth: 0,
                  border: 'none',
                  cursor: readOnly ? 'default' : 'pointer',
                  color: '#fff',
                  fontSize: 11,
                  lineHeight: 1.2,
                  // The number changes under the cursor while dragging;
                  // proportional digits make it jitter as well as change.
                  fontVariantNumeric: 'tabular-nums',
                  px: 0.5,
                  overflow: 'hidden',
                  backgroundColor: segmentColour(i, levels.length),
                  '&:focus-visible': { outline: '2px solid #000' },
                }}
              >
                {roomy && (
                  <>
                    <Box sx={{ fontWeight: 600 }} component="span">
                      {value}%
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        display: 'block',
                        opacity: 0.85,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {level}
                    </Box>
                  </>
                )}
              </Box>
            </Tooltip>
          );
        })}

        {!readOnly &&
          levels.slice(0, -1).map((level, i) => (
            <Box
              key={`boundary-${level}`}
              role="slider"
              tabIndex={0}
              aria-label={`Boundary between ${level} and ${levels[i + 1]}`}
              aria-valuemin={0}
              aria-valuemax={PERCENT_TOTAL}
              aria-valuenow={Math.round(offsets[i + 1])}
              aria-valuetext={`${values[i]}% ${level}, ${values[i + 1]}% ${
                levels[i + 1]
              }`}
              onPointerDown={startDrag(i)}
              onKeyDown={(e) => {
                const step = e.shiftKey ? 10 : 1;
                if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
                  e.preventDefault();
                  nudgeBoundary(i, -step);
                }
                if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
                  e.preventDefault();
                  nudgeBoundary(i, step);
                }
              }}
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${offsets[i + 1]}%`,
                width: 11,
                ml: '-5px',
                cursor: 'col-resize',
                touchAction: 'none',
                backgroundColor: 'transparent',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: 5,
                  width: 1,
                  backgroundColor: 'rgba(255,255,255,0.9)',
                },
                '&:hover::after, &:focus-visible::after': {
                  width: 3,
                  left: 4,
                  backgroundColor: '#000',
                },
              }}
            />
          ))}
      </Box>

      {/* Anything too thin to carry its own text still has to be readable. */}
      {values.some((v) => v < LABEL_MIN_PERCENT) && (
        <Box
          sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.75 }}
          aria-hidden
        >
          {levels.map((level, i) =>
            values[i] < LABEL_MIN_PERCENT ? (
              <Typography
                key={level}
                variant="caption"
                sx={{ color: 'text.secondary' }}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    width: 8,
                    height: 8,
                    borderRadius: '2px',
                    mr: 0.5,
                    backgroundColor: segmentColour(i, levels.length),
                  }}
                />
                {level} {values[i]}%
              </Typography>
            ) : null,
          )}
        </Box>
      )}

      <Popover
        open={!readOnly && editing !== null}
        anchorEl={
          editing !== null
            ? document.getElementById(`seg-${label}-${editing}`)
            : null
        }
        onClose={(_e: unknown, reason?: string) =>
          // The input's own Escape handler sets editing to null, but the
          // keydown still bubbles to MUI's Modal, which calls onClose — and
          // commitEntry would read the level from a closure that has not seen
          // that update yet, applying the value Escape meant to discard.
          reason === 'escapeKeyDown' ? setEditing(null) : commitEntry()
        }
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{ p: 1.5 }}>
          <TextField
            size="small"
            type="number"
            autoFocus
            label={`${label} percentage for ${levelLabel(editing)}`}
            value={entry}
            onChange={(e) => setEntry(e.target.value)}
            InputProps={{ endAdornment: '%' }}
            inputProps={{
              min: 0,
              max: 100,
              step: 1,
              // On the input itself: MUI spreads a bare onKeyDown onto the
              // FormControl root, which is not what has focus.
              onKeyDown: (e: React.KeyboardEvent) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitEntry();
                }
                if (e.key === 'Escape') setEditing(null);
              },
            }}
            helperText="The rest move to keep the total at 100%."
            sx={{ width: 230 }}
          />
        </Box>
      </Popover>
    </Box>
  );
};

export default DistributionBar;
